import {
    Text,
    Select,
    RadioGroup,
    Date as DateField,
    RadioGroupWithWritein,
    AutocompleteSelect,
} from 'components/forms/fields';
import Joi from 'joi';
import {countries} from 'components/forms/hooks/useCountries';
import states from 'components/forms/fields/statesUSA';
import {InputAdornment} from '@material-ui/core';

const tankermanItem = Joi.object({
    date: Joi.date().max('now').required(),
    transfer_type: Joi.string().required(),
    _signed: Joi.boolean().invalid(false, null, '').required(),
});

export default {
    //we want mmcNumber to accept null, but not allow alphabet characters. Only numbers or blank!

    mmcNumberRequired: {
        component: Text,
        validation: Joi.number()
            .integer()
            .required()
            .min(100000)
            .max(9999999)
            .messages({
                'number.min': 'MMC Reference Number must be 6 or 7 digits long',
                'number.max': 'MMC Reference Number must be 6 or 7 digits long',
                'number.base': 'Please enter your MMC Reference Number',
            }),
        initialValue: null,
        inputProps: {type: 'tel'},
        required: true,
    },
    mmcNumberOptional: {
        component: Text,
        validation: Joi.number()
            .integer()
            .min(100000)
            .max(9999999)
            .allow(null, '', 0)
            .messages({
                'number.min': 'MMC Reference Number must be 6 or 7 digits long',
                'number.max': 'MMC Reference Number must be 6 or 7 digits long',
            }),
        initialValue: null,
        inputProps: {type: 'tel'},
    },
    boolRequired: {
        component: RadioGroup,
        validation: Joi.boolean().required(),
        options: {true: 'Yes', false: 'No'},
        initialValue: '',
        row: true,
        required: true,
    },
    boolRequiredTrue: {
        component: RadioGroup,
        validation: Joi.boolean().required().invalid(false, 'false'),
        options: {true: 'Yes', false: 'No'},
        initialValue: '',
        row: true,
        required: true,
    },
    boolOptional: {
        component: RadioGroup,
        validation: Joi.boolean().allow('', null),
        options: {true: 'Yes', false: 'No'},
        initialValue: '',
        row: true,
        required: false,
    },
    dateRequired: {
        component: DateField,
        validation: Joi.date().required(),
        initialValue: '',
        inputProps: {type: 'tel'},
        required: true,
    },
    dateRequiredMaxToday: {
        component: DateField,
        validation: Joi.date()
            .min(new Date('January 1, 1900'))
            .max(new Date())
            .messages({
                'date.min': 'Please enter only dates from within the last century.',
                'date.max': 'Dates in the future not allowed.',
            }),
        initialValue: '',
        inputProps: {type: 'tel'},
        required: true,
        disableFuture: true,
    },
    dateOptionalMaxToday: {
        component: DateField,
        validation: Joi.date()
            .allow('', null)
            .min(new Date('January 1, 1900'))
            .max(new Date())
            .messages({
                'date.min': 'Please enter only dates from within the last century.',
                'date.max': 'Dates in the future not allowed.',
            }),
        initialValue: '',
        inputProps: {type: 'tel'},
        disableFuture: true,
    },
    dateOptional: {
        component: DateField,
        validation: Joi.string(),
        initialValue: '',
        inputProps: {type: 'tel'},
        required: false,
    },
    textRequired: {
        component: Text,
        validation: Joi.string().required(),
        initialValue: '',
        required: true,
    },
    textOptional: {
        component: Text,
        validation: Joi.string().allow('', null),
        initialValue: '',
    },
    middleInitialOptional: {
        component: Text,
        validation: Joi.string().max(1).allow('', null),
        initialValue: '',
    },
    socialSecurityNumber: {
        component: Text,
        validation: Joi.number()
            .integer()
            .min(100000000)
            .max(999999999)
            .allow(null)
            .messages({
                'number.min': 'Social Security Number must be 9 digits long',
                'number.max': 'Social Security Number must be 9 digits long',
            }),
        initialValue: null,
        inputProps: {type: 'tel'},
    },
    ssnLastFour: {
        component: Text,
        validation: Joi.string()
            .regex(new RegExp(/^[0-9]{4}$/))
            .messages({
                'string.pattern.base': 'Enter the last 4 digits of your SSN',
            }),
        initialValue: null,
        inputProps: {type: 'tel'},
    },
    legal_alien_number: {
        component: Text,
        validation: Joi.number().integer().min(100000000).max(999999999).messages({
            'number.min': 'Legal Alien Number must be 9 digits long',
            'number.max': 'Legal Alien Number must be 9 digits long',
        }),
        initialValue: null,
        inputProps: {type: 'tel'},
    },
    phoneRequired: {
        component: Text,
        validation: Joi.number().integer().min(1000000000).max(9999999999).messages({
            'number.min': 'Please enter a 10 digit US based phone number',
            'number.max': 'Please enter a 10 digit US based phone number',
        }),
        initialValue: '',
        required: true,
        inputProps: {type: 'tel'},
    },
    phoneOptional: {
        component: Text,
        validation: Joi.number()
            .integer()
            .min(1000000000)
            .max(9999999999)
            .allow('', null)
            .messages({
                'number.min': '10 digit US based phone number',
                'number.max': '10 digit US based phone number',
            }),
        initialValue: null,
        inputProps: {type: 'tel'},
    },
    satPhone: {
        component: Text,
        validation: Joi.number().integer(),
        initialValue: null,
        inputProps: {type: 'tel'},
    },
    emailRequired: {
        component: Text,
        validation: Joi.string().email({tlds: {allow: false}}),
        required: true,
    },
    emailOptional: {
        component: Text,
        validation: Joi.string()
            .email({tlds: {allow: false}})
            .allow('', null),
        required: false,
    },
    imoRequired: {
        component: Text,
        validation: Joi.number().integer().min(1000000).max(99999999).messages({
            'number.min': 'Please enter a 7 digit IMO number',
            'number.max': 'Please enter a 7 digit IMO number',
        }),
        initialValue: null,
        placeholder: '9270335',
        inputProps: {type: 'tel'},
        required: true,
    },
    imoOptional: {
        component: Text,
        validation: Joi.number()
            .integer()
            .min(1000000)
            .max(99999999)
            .messages({
                'number.min': 'Please enter a 7 digit IMO number',
                'number.max': 'Please enter a 7 digit IMO number',
            })
            .allow('', null),
        initialValue: null,
        placeholder: '9270335',
        inputProps: {type: 'tel'},
    },
    mmsiOptional: {
        component: Text,
        validation: Joi.string().min(9).max(9).allow('', null).messages({
            'string.min': 'Please enter a valid 9 digit MMSI number.',
            'string.max': 'Please enter a valid 9 digit MMSI number.',
        }),
        initialValue: '',
        placeholder: '369959000',
        inputProps: {type: 'tel'},
    },
    uscgOfficialNumberOptional: {
        component: Text,
        validation: Joi.string().max(13).allow('', null).messages({
            'string.max':
                'Please enter a valid USCG Official Number up to 13 characters.',
        }),
        placeholder: '248292',
        initialValue: '',
        inputProps: {type: 'tel'},
    },
    stateRegistrationNumberOptional: {
        component: Text,
        validation: Joi.string().max(22).allow('', null).messages({
            'string.max':
                'Please enter a valid USCG Official Number up to 13 characters.',
        }),
        initialValue: '',
        placeholder: 'WN 3717 ZW',
    },
    floatRequired: {
        component: Text,
        validation: Joi.number().positive().required(),
        initialValue: '',
        inputProps: {type: 'tel'},
        required: true,
    },
    floatOptional: {
        component: Text,
        validation: Joi.number().positive().allow('', null),
        initialValue: '',
        inputProps: {type: 'tel'},
        required: false,
    },
    integerRequired: {
        component: Text,
        validation: Joi.number().positive().integer().required(),
        initialValue: '',
        inputProps: {type: 'number'},
        required: true,
    },
    integerRequiredZeroOrMore: {
        component: Text,
        validation: Joi.number().integer().min(0).required(),
        initialValue: '',
        inputProps: {type: 'number'},
        required: true,
    },
    integerOptional: {
        component: Text,
        validation: Joi.number().integer().min(0).allow(null, ''),
        initialValue: '',
        inputProps: {type: 'number'},
        required: false,
    },
    tonnageTypeRequired: {
        component: Select,
        validation: Joi.string().required(),
        options: {
            grt: 'GRT',
            gt: 'GT',
            nrt: 'NRT',
        },
        initialValue: 'grt',
        required: true,
    },
    tonnageTypeRequiredToss: {
        component: Select,
        validation: Joi.string().required(),
        options: {
            grt: 'Gross Registered Tonnage',
            gt: 'Gross Tonnage or ITC',
            displacement: 'Displacement',
        },
        initialValue: 'grt',
        required: true,
    },
    country: {
        component: Select,
        validation: Joi.string(),
        options: countries,
        initialValue: 'US',
        required: true,
    },
    states: {
        component: AutocompleteSelect,
        validation: Joi.string().min(2).max(2),
        options: states?.map(x => x.abbreviation),
        initialValue: '',
        required: true,
    },
    selectRequired: {
        component: Select,
        validation: Joi.string(),
        initialValue: '',
        required: true,
    },
    selectOptional: {
        component: Select,
        validation: Joi.string().allow(''),
        initialValue: '',
    },
    radioWithWritin: {
        component: RadioGroupWithWritein,
        validation: Joi.string(),
        initialValue: '',
    },
    radioRequired: {
        component: RadioGroup,
        validation: Joi.string(),
        initialValue: '',
        required: true,
    },
    dollarAmountRequired: {
        component: Text,
        validation: Joi.number().messages({
            'number.base': 'Please enter numbers only, like 12.34',
        }),
        initialValue: '',
        inputProps: {
            type: 'tel',
        },
        InputProps: {
            startAdornment: <InputAdornment position='start'>$</InputAdornment>,
        },
        required: true,
    },
    tankermanArray: {
        initialValue: [],
        validation: Joi.array().items(tankermanItem),
        required: true,
    },
    autocompleteSelectRequired: {
        component: AutocompleteSelect,
        initialValue: '',
        required: true,
    },
};

const emailSchema = Joi.object({
    email: Joi.string().email({tlds: {allow: false}}),
});
export {emailSchema};
